// Core game types for SeekingTheWord

/** Represents a position on the grid */
export interface Position {
  row: number;
  col: number;
}

/** Direction vectors for word placement */
export interface Direction {
  dRow: number;
  dCol: number;
}

/** All possible directions for word placement (8 standard directions) */
export const DIRECTIONS: Record<string, Direction> = {
  RIGHT: { dRow: 0, dCol: 1 },         // Horizontal left to right
  LEFT: { dRow: 0, dCol: -1 },         // Horizontal right to left
  DOWN: { dRow: 1, dCol: 0 },          // Vertical top to bottom
  UP: { dRow: -1, dCol: 0 },           // Vertical bottom to top
  DOWN_RIGHT: { dRow: 1, dCol: 1 },    // Diagonal top-left to bottom-right
  UP_LEFT: { dRow: -1, dCol: -1 },     // Diagonal bottom-right to top-left
  DOWN_LEFT: { dRow: 1, dCol: -1 },    // Diagonal top-right to bottom-left
  UP_RIGHT: { dRow: -1, dCol: 1 },     // Diagonal bottom-left to top-right
};

/** Array of all direction values for iteration */
export const ALL_DIRECTIONS: Direction[] = Object.values(DIRECTIONS);

/** A placed word on the grid with its path */
export interface PlacedWord {
  word: string;
  path: Position[];  // The positions occupied by each letter
}

/** A single cell in the grid */
export interface GridCell {
  letter: string;
  position: Position;
  isPartOfWord: boolean;  // True if this cell is part of a hidden word
  wordIndex?: number;     // Which word this cell belongs to (if any)
}

/** The complete game grid */
export interface GameGrid {
  cells: GridCell[][];
  size: number;
}

/** Game state tracking */
export interface GameState {
  grid: GameGrid;
  hiddenWords: PlacedWord[];
  foundWords: string[];
  currentSelection: Position[];
  isSelecting: boolean;
  gameOver: boolean;
  showWinModal: boolean;
}

/** Selection state during drag */
export interface SelectionState {
  isSelecting: boolean;
  startPosition: Position | null;
  currentPath: Position[];
}

/** Grid configuration constants */
export const GRID_SIZE = 10;
export const WORD_COUNT = 10;

/** Get the string from a selection path on the grid */
export function getWordFromPath(grid: GameGrid, path: Position[]): string {
  return path.map(pos => grid.cells[pos.row][pos.col].letter).join('');
}

/** Check if two positions are equal */
export function positionsEqual(a: Position, b: Position): boolean {
  return a.row === b.row && a.col === b.col;
}

/** Check if a position is within grid bounds */
export function isInBounds(pos: Position, size: number = GRID_SIZE): boolean {
  return pos.row >= 0 && pos.row < size && pos.col >= 0 && pos.col < size;
}

/** Check if two positions are adjacent (including diagonals) */
export function areAdjacent(a: Position, b: Position): boolean {
  const rowDiff = Math.abs(a.row - b.row);
  const colDiff = Math.abs(a.col - b.col);
  return rowDiff <= 1 && colDiff <= 1 && !(rowDiff === 0 && colDiff === 0);
}
